import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "vehicle-types-table-container" };
const _hoisted_2 = {
    key: 0,
    class: "mobile-layout"
};
const _hoisted_3 = { class: "mobile-sort-controls" };
const _hoisted_4 = { class: "mobile-vehicle-list" };
const _hoisted_5 = ["onClick"];
const _hoisted_6 = { class: "mobile-vehicle-content" };
const _hoisted_7 = { class: "mobile-vehicle-header" };
const _hoisted_8 = { class: "mobile-vehicle-details" };
const _hoisted_9 = { class: "mobile-vehicle-variant" };
const _hoisted_10 = { class: "mobile-vehicle-years" };
const _hoisted_11 = {
    key: 1,
    class: "vehicle-types-table"
};
const _hoisted_12 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_13 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_14 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_15 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_16 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_17 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_18 = ["onClick"];
const _hoisted_19 = { key: 0 };
const _hoisted_20 = {
    key: 0,
    class: "no-results"
};
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useMakeModelSearch } from '../../composables/useMakeModelSearch';
export default /*@__PURE__*/ _defineComponent({
    __name: 'VehicleTypesTable',
    props: {
        vehicleTypes: {},
        onVehicleSelect: { type: Function },
        searchQuery: {},
        selectedYearFilter: {}
    },
    setup(__props) {
        const props = __props;
        // Current year for open-ended ranges
        const CURRENT_YEAR = new Date().getFullYear();
        // Helpers to normalize numbers
        const toNum = (v) => {
            if (v === null || v === undefined || v === '')
                return undefined;
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
        };
        const { selectedMake, selectedModel } = useMakeModelSearch();
        // Check if vehicle type matches year filter
        const matchesYearFilter = (type) => {
            const val = props.selectedYearFilter;
            if (!val)
                return true;
            const start = toNum(type.year_start);
            const end = toNum(type.year_end) ?? CURRENT_YEAR;
            if (start === undefined)
                return false;
            if (val === 'before-1994') {
                return end < 1994;
            }
            if (val === 'after-1994') {
                return start >= 1994;
            }
            // Single-year selection, e.g., "2018"
            if (/^\d{4}$/.test(val)) {
                const year = Number(val);
                return start <= year && end >= year;
            }
            return true;
        };
        // Mobile detection
        const isMobile = ref(false);
        const checkMobile = () => {
            isMobile.value = window.innerWidth < 768; // md breakpoint
        };
        onMounted(() => {
            checkMobile();
            window.addEventListener('resize', checkMobile);
        });
        onUnmounted(() => {
            window.removeEventListener('resize', checkMobile);
        });
        // Sorting logic
        const sortKey = ref('title');
        const sortDirection = ref('asc');
        // Filter vehicle types based on search query and year filter
        const filteredVehicleTypes = computed(() => {
            let filtered = props.vehicleTypes;
            if (props.searchQuery) {
                const q = props.searchQuery.toLowerCase();
                filtered = filtered.filter(type => type.title.toLowerCase().includes(q) ||
                    (type.make_title && type.make_title.toLowerCase().includes(q)) ||
                    (type.model_title && type.model_title.toLowerCase().includes(q)) ||
                    (type.cc && type.cc.toString().includes(q)) ||
                    (type.power_kw && type.power_kw.toString().includes(q)) ||
                    (type.power_hp && type.power_hp.toString().includes(q)) ||
                    (type.fuel && type.fuel.toLowerCase().includes(q)));
            }
            // Filter by year
            filtered = filtered.filter(matchesYearFilter);
            return filtered;
        });
        const sortedVehicleTypes = computed(() => {
            return [...filteredVehicleTypes.value].sort((a, b) => {
                let aVal = a[sortKey.value];
                let bVal = b[sortKey.value];
                // Handle year_start and year_end specially
                if (sortKey.value === 'years') {
                    aVal = `${a.year_start}-${a.year_end}`;
                    bVal = `${b.year_start}-${b.year_end}`;
                }
                // Fallback values
                if (sortKey.value === 'make_title') {
                    aVal = a.make_title ?? selectedMake.value?.title ?? '';
                    bVal = b.make_title ?? selectedMake.value?.title ?? '';
                }
                if (sortKey.value === 'model_title') {
                    aVal = a.model_title ?? selectedModel.value?.title ?? '';
                    bVal = b.model_title ?? selectedModel.value?.title ?? '';
                }
                if (aVal < bVal)
                    return sortDirection.value === 'asc' ? -1 : 1;
                if (aVal > bVal)
                    return sortDirection.value === 'asc' ? 1 : -1;
                return 0;
            });
        });
        const sortBy = (key) => {
            if (sortKey.value === key) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            }
            else {
                sortKey.value = key;
                sortDirection.value = 'asc';
            }
        };
        // Mobile sorting methods
        const handleSortChange = () => {
            sortDirection.value = 'asc'; // Reset to ascending when changing sort key
        };
        const toggleSortDirection = () => {
            sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
        };
        // Fallback to selected make/model from MakeModelSearch when API returns null
        function displayMakeTitle(type) {
            return type.make_title ?? selectedMake.value?.title ?? '';
        }
        function displayModelTitle(type) {
            return type.model_title ?? selectedModel.value?.title ?? '';
        }
        // Handle click on a vehicle type
        function clickVehicleType(type) {
            props.onVehicleSelect(type);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    (isMobile.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _withDirectives(_createElementVNode("select", {
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((sortKey).value = $event)),
                                    onChange: handleSortChange,
                                    class: "sort-dropdown"
                                }, [...(_cache[7] || (_cache[7] = [
                                        _createStaticVNode("<option value=\"title\" data-v-0c94bfa6>Sort by Name</option><option value=\"cc\" data-v-0c94bfa6>Sort by cc</option><option value=\"power_kw\" data-v-0c94bfa6>Sort by power_kw</option><option value=\"power_hp\" data-v-0c94bfa6>Sort by power_hp</option><option value=\"fuel\" data-v-0c94bfa6>Sort by fuel</option><option value=\"years\" data-v-0c94bfa6>Sort by Years</option>", 6)
                                    ]))], 544), [
                                    [_vModelSelect, sortKey.value]
                                ]),
                                _createElementVNode("button", {
                                    onClick: toggleSortDirection,
                                    class: "sort-direction-btn"
                                }, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1)
                            ]),
                            _createElementVNode("div", _hoisted_4, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedVehicleTypes.value, (type) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: type.id,
                                        onClick: ($event) => (clickVehicleType(type)),
                                        class: "mobile-vehicle-item"
                                    }, [
                                        _createElementVNode("div", _hoisted_6, [
                                            _createElementVNode("div", _hoisted_7, [
                                                _createElementVNode("strong", null, _toDisplayString(displayMakeTitle(type)) + " " + _toDisplayString(displayModelTitle(type)), 1)
                                            ]),
                                            _createElementVNode("div", _hoisted_8, [
                                                _createElementVNode("div", _hoisted_9, _toDisplayString(type.title), 1),
                                                _createElementVNode("div", _hoisted_10, _toDisplayString(type.year_start) + " - " + _toDisplayString(type.year_end), 1)
                                            ])
                                        ]),
                                        _cache[8] || (_cache[8] = _createElementVNode("button", { class: "mobile-arrow-button" }, [
                                            _createElementVNode("div", { class: "vehicle-card__arrow-bg" }),
                                            _createElementVNode("svg", {
                                                class: "vehicle-card__arrow-icon",
                                                xmlns: "http://www.w3.org/2000/svg",
                                                width: "19",
                                                height: "34",
                                                viewBox: "0 0 19 34",
                                                fill: "none"
                                            }, [
                                                _createElementVNode("path", {
                                                    d: "M0.707031 0.707153L16.707 16.7072L0.707031 32.7072",
                                                    stroke: "#33363F",
                                                    "stroke-width": "2"
                                                })
                                            ])
                                        ], -1))
                                    ], 8, _hoisted_5));
                                }), 128))
                            ])
                        ]))
                        : (_openBlock(), _createElementBlock("table", _hoisted_11, [
                            _createElementVNode("thead", null, [
                                _createElementVNode("tr", null, [
                                    _createElementVNode("th", {
                                        onClick: _cache[1] || (_cache[1] = ($event) => (sortBy('title'))),
                                        class: "sortable"
                                    }, [
                                        _cache[9] || (_cache[9] = _createTextVNode(" Name ", -1)),
                                        (sortKey.value === 'name')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("th", {
                                        onClick: _cache[2] || (_cache[2] = ($event) => (sortBy('cc'))),
                                        class: "sortable"
                                    }, [
                                        _cache[10] || (_cache[10] = _createTextVNode(" cc ", -1)),
                                        (sortKey.value === 'cc')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("th", {
                                        onClick: _cache[3] || (_cache[3] = ($event) => (sortBy('power_kw'))),
                                        class: "sortable"
                                    }, [
                                        _cache[11] || (_cache[11] = _createTextVNode(" Power (kW) ", -1)),
                                        (sortKey.value === 'power_kw')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("th", {
                                        onClick: _cache[4] || (_cache[4] = ($event) => (sortBy('power_hp'))),
                                        class: "sortable"
                                    }, [
                                        _cache[12] || (_cache[12] = _createTextVNode(" Power (hp) ", -1)),
                                        (sortKey.value === 'power_hp')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("th", {
                                        onClick: _cache[5] || (_cache[5] = ($event) => (sortBy('fuel'))),
                                        class: "sortable"
                                    }, [
                                        _cache[13] || (_cache[13] = _createTextVNode(" Fuel ", -1)),
                                        (sortKey.value === 'fuel')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _createElementVNode("th", {
                                        onClick: _cache[6] || (_cache[6] = ($event) => (sortBy('years'))),
                                        class: "sortable"
                                    }, [
                                        _cache[14] || (_cache[14] = _createTextVNode(" Years ", -1)),
                                        (sortKey.value === 'years')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(sortDirection.value === 'asc' ? '▲' : '▼'), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _cache[15] || (_cache[15] = _createElementVNode("th", null, " ", -1))
                                ])
                            ]),
                            _createElementVNode("tbody", null, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedVehicleTypes.value, (type) => {
                                    return (_openBlock(), _createElementBlock("tr", {
                                        key: type.id,
                                        onClick: ($event) => (clickVehicleType(type)),
                                        class: "vehicle-row"
                                    }, [
                                        _createElementVNode("td", null, _toDisplayString(type.title), 1),
                                        _createElementVNode("td", null, _toDisplayString(type.cc), 1),
                                        _createElementVNode("td", null, _toDisplayString(type.power_kw), 1),
                                        _createElementVNode("td", null, _toDisplayString(type.power_hp), 1),
                                        _createElementVNode("td", null, _toDisplayString(type.fuel), 1),
                                        _createElementVNode("td", null, [
                                            _createTextVNode(_toDisplayString(type.year_start) + " ", 1),
                                            (type.year_end)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_19, " - " + _toDisplayString(type.year_end), 1))
                                                : _createCommentVNode("", true)
                                        ]),
                                        _cache[16] || (_cache[16] = _createElementVNode("td", null, [
                                            _createElementVNode("button", { class: "arrow-button" }, [
                                                _createElementVNode("div", { class: "vehicle-card__arrow-bg" }),
                                                _createElementVNode("svg", {
                                                    class: "vehicle-card__arrow-icon",
                                                    xmlns: "http://www.w3.org/2000/svg",
                                                    width: "19",
                                                    height: "34",
                                                    viewBox: "0 0 19 34",
                                                    fill: "none"
                                                }, [
                                                    _createElementVNode("path", {
                                                        d: "M0.707031 0.707153L16.707 16.7072L0.707031 32.7072",
                                                        stroke: "#33363F",
                                                        "stroke-width": "2"
                                                    })
                                                ])
                                            ])
                                        ], -1))
                                    ], 8, _hoisted_18));
                                }), 128))
                            ])
                        ]))
                ]),
                (sortedVehicleTypes.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [...(_cache[17] || (_cache[17] = [
                            _createElementVNode("p", null, "No vehicle types found matching your search criteria.", -1)
                        ]))]))
                    : _createCommentVNode("", true)
            ], 64));
        };
    }
});
